import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { updateReportParticipants } from "../../../../api/service/report";
import { ReportAccessDialog } from "./ReportAccessDialog";
import { WorkOrderParticipantStatus } from "../../workOrders/utils";

/**
 * Tilknyttede utførende (utover ansvarlig utførende på avtalen).
 */
function reportUpdateErrorMessage(res, fallback) {
  if (!res?.response) return fallback;
  const data = res.response?.data;
  if (Array.isArray(data?.errors)) return data.errors.join(", ");
  if (typeof data?.error === "string") return data.error;
  return fallback;
}

export const ReportAccessesSection = ({
  reportId,
  participants = [],
  onParticipantsChange,
  canManage,
  executingEmployeeId,
  className = "",
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleDelete = async (participantId) => {
    if (!window.confirm(t("report.participants.delete_confirm"))) return;

    try {
      setError(null);
      const updated = (participants || []).map((p) =>
        p.id === participantId ? { ...p, delete: true } : p
      );
      const saveRes = await updateReportParticipants(reportId, updated);
      if (saveRes?.response) {
        setError(
          reportUpdateErrorMessage(saveRes, t("report.participants.delete_error"))
        );
        return;
      }
      const list = saveRes?.data?.participants;
      if (!Array.isArray(list)) {
        setError(t("report.participants.delete_error"));
        return;
      }
      onParticipantsChange(list);
    } catch (err) {
      console.error(err);
      setError(t("report.participants.delete_error"));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSuccess = (list) => {
    if (onParticipantsChange && Array.isArray(list)) {
      onParticipantsChange(list);
    }
    handleDialogClose();
  };

  const initials = (name) =>
    String(name || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w.charAt(0))
      .join("")
      .toUpperCase() || "?";

  return (
    <>
      <div
        className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-slate-900">
              {t("report.participants.title")}
            </h3>
            <p className="mt-0.5 text-[13px] leading-snug text-slate-500">
              {t("report.participants.subtitle")}
            </p>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              <PlusIcon className="h-4 w-4" />
              {t("report.participants.add")}
            </button>
          )}
        </div>
        <div className="px-5 py-4">
          {error && (
            <div className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">
              {error}
            </div>
          )}
          {participants.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/70 px-4 py-7 text-center text-[13px] text-slate-500">
              <p>{t("report.participants.none")}</p>
              {canManage && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                  className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("report.participants.add_first")}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {participants.map((p) => {
                const name = p.displayTitle || p.user?.displayTitle || "—";
                return (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3.5 py-3 transition-colors hover:bg-slate-50/70"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-blue-50 text-[12px] font-bold text-blue-700">
                        {initials(name)}
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="min-w-0 truncate text-[14px] font-semibold text-slate-900">
                            {p.user ? (
                              <Link
                                to={`/dashboard/users/${p.user.id}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {name}
                              </Link>
                            ) : (
                              name
                            )}
                          </span>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                            {p.role || "—"}
                          </span>
                        </div>
                        <div className="mt-1">
                          <WorkOrderParticipantStatus participant={p} />
                        </div>
                      </div>
                    </div>
                    {canManage && (
                      <div className="ml-2 flex shrink-0 items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(p);
                            setDialogOpen(true);
                          }}
                          className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                          title={t("report.participants.edit")}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(p.id)}
                          className="inline-flex items-center rounded-lg border border-rose-200 bg-white p-2 text-rose-600 transition-colors hover:bg-rose-50"
                          title={t("report.participants.delete")}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ReportAccessDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        reportId={reportId}
        access={editing}
        onSuccess={handleSuccess}
        executingEmployeeId={executingEmployeeId}
        participants={participants}
      />
    </>
  );
};
